// ============================================================
// What a press, a drag, a wheel and a drop on the map mean
// ============================================================
// Every pointer event on the scene arrives here with `ctx`, the map's live
// state for this render: the mode flags, the gesture refs, the hooks'
// handlers and the props' callbacks. Each gesture is a step that answers
// "was this mine?"; the first to claim a press ends it, so the ORDER of
// the steps is the grammar of the map and is kept exactly as it grew.
//
// The browser's hit test knows what is under the pointer and a
// ground-plane probe does not, which is why so much here asks
// `closest('[data-token-id]')` or `data-solid-id` before unprojecting.

import { draggingCamera } from '../../utils/cameraDrag';
import {
  markerIdOf,
  sweepDown,
  replacePickDown,
  plantDown,
  sweepMove,
  formationMove,
  sweepUp,
  formationUp,
  dragOver,
  drop,
} from './deployGestures';
import { solidBounds } from '../../engine/battlefield/solids';
import { pointerToScene } from '../../utils/scenePointer';
import { solidUnderPointer } from '../../utils/solidUnder';

/**
 * Is this drag meant for the CAMERA rather than for the active tool?
 *
 * MIDDLE-DRAG pans and SHIFT-LEFT pans, in every mode - the laptop with no
 * middle button still has to be able to move the map. RIGHT-DRAG orbits,
 * except where right-drag already means something (Build's erase, Deploy's
 * sweep); there it takes a Shift. The wheel zooms everywhere already.
 */
function cameraGesture(e, rightIsTaken) {
  if (e.button === 1) return true;
  if (e.button === 0) return e.shiftKey;
  if (e.button === 2) return !rightIsTaken || e.shiftKey;
  return false;
}

/**
 * WHERE THE BUTTON WENT DOWN, worked out NOW - for the ping, which is
 * committed later, when the camera may have moved (orbit starts on this
 * very event). A MARKER FIRST, then a structure, then the floor:
 * unprojecting onto y=0 answers with the floor BEHIND anything that has
 * height, which is a marker's body at every pitch but straight down.
 */
function pressPointOf(ctx, e) {
  const pressedToken = ctx.tokens[markerIdOf(e.target)];
  if (pressedToken) return { at: { x: pressedToken.x, z: pressedToken.z }, y: pressedToken.elevation || 0 };
  const pressedSolid = solidUnder(ctx, e);
  const pressY = pressedSolid ? solidBounds(pressedSolid).max.y : 0;
  return {
    at: ctx.deployment_.groundPoint(e, pressY),
    y: pressY,
    // WHAT TO ASK ABOUT, which is not always where it lands. Unprojecting
    // onto a solid's TOP plane lands just past its footprint for its SIDE,
    // so "what is here" would find nothing there; the pressed solid answers
    // that half.
    ...(pressedSolid ? { describeAt: { x: pressedSolid.position.x, z: pressedSolid.position.z } } : {}),
  };
}

/** The solid under the pointer, on whichever renderer draws the terrain (utils/solidUnder). */
const solidUnder = (ctx, e) =>
  solidUnderPointer(e, { solids: ctx.solids, camera: ctx.getCamera(), includeHidden: !!ctx.isGm });

/** Note the element, the left press and the press point, for the gestures that end later. */
function notePress(ctx, e) {
  ctx.sceneEl.current = e.currentTarget;
  if (e.button === 0) ctx.leftPress.current = { x: e.clientX, y: e.clientY };
  ctx.pressPoint.current = pressPointOf(ctx, e);
}

/** THE CAMERA GETS THE OTHER TWO BUTTONS, which leaves the left one free for the work. */
function cameraDown(ctx, e) {
  if (!cameraGesture(e, (ctx.building && !ctx.looking) || ctx.deploying)) return false;
  ctx.cam.onPointerDown(e);
  return true;
}

/**
 * CLICK A STRUCTURE TO SELECT IT, bare ground to place one. The faces
 * already carry `data-solid-id` - it is how the deploy marker finds a
 * rooftop - so the hit test exists and only ever needed asking (v8.1.0);
 * on the GPU map it is a line of sight (utils/solidUnder).
 */
function structureDown(ctx, e) {
  if (!ctx.shaping || e.button !== 0) return false;
  e.preventDefault();
  const hit = solidUnder(ctx, e)?.id;
  if (hit) {
    ctx.build.setSelected(hit);
    ctx.structureDrag.current = { id: e.pointerId, solidId: hit, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  } else {
    const at = ctx.groundPointFor(e);
    if (at) ctx.build.placeStructure(at);
  }
  return true;
}

/**
 * Build takes the right button too - that is its erase, and it must be
 * tested AFTER the camera and BEFORE the left-button-only rule below, or
 * right-drag erases nothing.
 */
function buildDown(ctx, e) {
  if (!ctx.layingCubes) return false;
  if (e.button !== 0 && e.button !== 2) return true;
  e.preventDefault();
  ctx.build.buildHandlers.onPointerDown(e);
  return true;
}

/**
 * Anything else pulls out the tape. A marker takes its own press only when
 * it is claiming the move gesture, and the tape magnets to that same
 * marker, so "measure from this man" needs no special case at all.
 */
function measureDown(ctx, e) {
  if (e.button !== 0) return true;
  ctx.setApplied(false);
  ctx.measureHandlers.onPointerDown(e);
  return true;
}

const DOWN_STEPS = [cameraDown, structureDown, sweepDown, replacePickDown, plantDown, buildDown, measureDown];

function pointerDown(ctx, e) {
  notePress(ctx, e);
  // A ping shares the right button with orbit: this only ever WATCHES, and
  // takes the gesture back if it was a press rather than a drag.
  ctx.pingGesture.onPointerDown(e);
  DOWN_STEPS.some((step) => step(ctx, e));
}

/** MOVING A STRUCTURE: the ground point under the pointer - a pillar does not live on the grid. */
function structureMove(ctx, e) {
  const dragging = ctx.structureDrag.current;
  if (dragging?.id !== e.pointerId) return false;
  const at = ctx.groundPointFor(e);
  if (at) {
    dragging.moved = true;
    ctx.build.patchStructure({ position: { x: at.x, y: 0, z: at.z } });
  }
  return true;
}

/**
 * What the cursor is over, for the inspect readout - also while building.
 * THE FACE UNDER THE POINTER FIRST, and the ground only when there is none:
 * a ground probe answers with the ground BEHIND anything with height, so
 * hovering a wall described the empty yard past it. Only the CELL, and only
 * when it changes: a move inside the same cell describes the same thing.
 */
function hoverMove(ctx, e) {
  if (!ctx.hoverInfo || ctx.deploying) return;
  if (draggingCamera(e)) putReadoutAway(ctx);
  else hoverOver(ctx, e);
}

/**
 * A camera drag sweeps the ground under a still pointer: nothing is being looked at, so nothing is asked (on the GPU
 * map each ask is a cast line of sight - an orbit spent a sixth of a second in ten on them), and the readout is put
 * away rather than left naming ground the pointer has left.
 */
function putReadoutAway(ctx) {
  if (ctx.hoverCell) ctx.setHoverCell(null);
}

/** The cell and the face under the pointer, into the readout. */
function hoverOver(ctx, e) {
  const ground = ctx.deployment_.groundPoint(e);
  if (!ground) return;
  const cx = Math.floor(ground.x / ctx.tile);
  const cz = Math.floor(ground.z / ctx.tile);
  // The face under the pointer is asked only when the cell changes (on the GPU map it is a cast line of sight, and a
  // move inside one cell describes the same thing) - and asked NOW: the event's target is gone by the time a state
  // updater runs.
  const was = ctx.hoverCell;
  if (was && was.cx === cx && was.cz === cz) return;
  const cell = { cx, cz, x: ground.x, z: ground.z, solidId: solidUnder(ctx, e)?.id ?? null };
  ctx.setHoverCell((prev) => (prev && prev.cx === cx && prev.cz === cz ? prev : cell));
}

/** Play's three gestures - the tape, the heading, the marker - each ignoring what is not theirs. */
function forwardToPlay(ctx, name, e) {
  ctx.measureHandlers[name](e);
  ctx.headingDragHandlers[name](e);
  ctx.tokenDragHandlers[name](e);
}

/** The tool's own handler while building, Play's three otherwise. */
function forwardToTool(ctx, name, e) {
  if (ctx.building) ctx.build.buildHandlers[name](e);
  else forwardToPlay(ctx, name, e);
}

/** The pointer for the take-cover cue: where it is on the scene (the screens' tooltip) and on the ground (a reach tile). */
function coverPointerOf(ctx, e) {
  return { ...pointerToScene(e), ground: ctx.deployment_?.groundPoint?.(e) ?? null };
}

function pointerMove(ctx, e) {
  // The camera handler is always live: it ignores anything that is not its
  // own captured drag, so it can simply run alongside.
  ctx.cam.onPointerMove(e);
  ctx.pingGesture.onPointerMove(e);
  if (structureMove(ctx, e) || sweepMove(ctx, e)) return;
  formationMove(ctx, e);
  hoverMove(ctx, e);
  // The take-cover cue's tooltip (2026-10-25): where the pointer is on the scene, for the screens' hit test. Not
  // while the camera is dragged: the ground slides under a still pointer, and a tooltip chasing it re-rendered the
  // whole panel mid-orbit (the stutter at the table, 2026-10-29) - it is put away instead.
  ctx.cover?.setCoverPointer?.(draggingCamera(e) ? null : coverPointerOf(ctx, e));
  forwardToTool(ctx, 'onPointerMove', e);
}

function structureUp(ctx, e) {
  if (ctx.structureDrag.current?.id !== e.pointerId) return false;
  e.currentTarget.releasePointerCapture?.(e.pointerId);
  ctx.structureDrag.current = null;
  return true;
}

/**
 * A LEFT CLICK ON BARE GROUND PUTS THE TAPE AWAY - Esc was a keyboard
 * answer to a question asked with the mouse - and, in the rails, closes the
 * panel open over the map (plan design-gaps, Z6c; the session page). A press
 * that travelled was a measurement and is left alone; one on a marker belongs
 * to the marker.
 */
function isBareGroundClick(ctx, e) {
  if (e.button !== 0 || ctx.building || !ctx.pressPoint.current) return false;
  if (e.target?.closest?.('[data-token-id]')) return false;
  const press = ctx.leftPress.current;
  return Math.hypot(e.clientX - (press?.x ?? 0), e.clientY - (press?.y ?? 0)) <= 4;
}

function pointerUp(ctx, e) {
  ctx.cam.onPointerUp(e);
  ctx.pingGesture.onPointerUp(e);
  if (sweepUp(ctx, e) || formationUp(ctx, e) || structureUp(ctx, e)) return;
  if (isBareGroundClick(ctx, e)) {
    ctx.clearLine();
    ctx.openRail?.(null);
  }
  forwardToTool(ctx, 'onPointerUp', e);
}

function pointerCancel(ctx, e) {
  ctx.cam.onPointerUp(e);
  ctx.pingGesture.onPointerCancel();
  forwardToTool(ctx, 'onPointerCancel', e);
}

/**
 * ON THE GROUND ONLY: a marker's own clicks are counted, and a double-click
 * that also moved the camera would fight that vocabulary. In Deploy it
 * PLANTS AND BRINGS THEM ON in one gesture (v8.21.0); elsewhere it
 * re-centres the camera there.
 */
function doubleClick(ctx, e) {
  if (e.target?.closest?.('[data-token-id]')) return;
  if (ctx.deploying) {
    ctx.deployment_.plantAt(e);
    if (ctx.activeRoomId) ctx.onDeployRoom?.(ctx.activeRoomId);
    return;
  }
  const at = ctx.deployment_.groundPoint(e);
  if (at) ctx.setCamera((c) => ({ ...c, target: { x: at.x, z: at.z } }));
}

/**
 * The wheel means altitude when a gesture owns it, and zooms otherwise.
 *
 * SHIFT AND THE WHEEL RAISE THE BANNER in Deploy, anywhere on the map
 * (v8.32.0). Not "point at the flag and wheel", as v8.31.0 shipped: the
 * overlay is `pointer-events: none` so the flag can never be hit-tested,
 * and measuring against the ground under the cursor drifts as it climbs.
 * Not the left button either: the press has already planted the flag.
 * Then a carried marker's height, then the level being built at.
 */
function wheel(ctx, e) {
  if (ctx.deploying && ctx.deployment && e.shiftKey) {
    // No preventDefault: React's wheel listener is PASSIVE, so the call
    // was refused - with a console error on every notch (./dev fuzz,
    // v8.399.4) - and the zoom beside it has never needed one.
    ctx.deployment_.setAir((air, step) => air + (e.deltaY < 0 ? step : -step));
    return;
  }
  if (ctx.onTokenWheel(e)) return;
  if (ctx.layingCubes && ctx.build.onBuildWheel(e)) return;
  ctx.cam.onWheel(e);
}

/**
 * RECALL, the inverse of deploying - otherwise nothing can take somebody
 * off the map without deleting them outright.
 *
 * WHAT IS UNDER THE POINTER, asked of the browser - not `e.target`.
 * Chromium retargets `contextmenu` to the nearest ancestor that is itself
 * hittable, so on a marker whose group is `pointer-events: none` the
 * target is the whole SVG. Measured, not reasoned about (v8.22.0).
 */
function contextMenu(ctx, e) {
  const under = document.elementFromPoint(e.clientX, e.clientY);
  const hit = ctx.deploying && under?.closest?.('[data-token-id]');
  e.preventDefault();
  if (hit) ctx.onRecall?.(hit.getAttribute('data-token-id'));
}

/**
 * The scene's event props for this render. The drag-and-drop three exist
 * only in Deploy: absent, the browser refuses the drop by itself.
 */
export function sceneHandlers(ctx) {
  const on = (handler) => (e) => handler(ctx, e);
  const deployOnly = (handler) => (ctx.deploying ? handler : undefined);
  return {
    onPointerDown: on(pointerDown),
    onPointerMove: on(pointerMove),
    onDoubleClick: on(doubleClick),
    onPointerUp: on(pointerUp),
    onPointerCancel: on(pointerCancel),
    onWheel: on(wheel),
    onPointerLeave: () => {
      ctx.setHoverCell(null);
      ctx.cover?.setCoverPointer?.(null);
    },
    onContextMenu: on(contextMenu),
    onDragOver: deployOnly(on(dragOver)),
    onDragLeave: deployOnly(() => ctx.setDropOverId(null)),
    onDrop: deployOnly(on(drop)),
  };
}
